import { Button } from "@/components/ui/button"
import { useTranslation } from "react-i18next"
import { usePlans } from "@/hooks/use-plans"
import { usePromoCode } from "@/hooks/use-promo-code"
import { useEffect, useState } from "react"
import { track, EVENTS } from "@/lib/mixpanel"
import { getStripeLookupKey } from "@/lib/checkout"
import FloatingCountdown from "@/components/common/FloatingCountdown"
import { PromoBanner } from "@/components/common/PromoBanner"
import { formatDiscount } from "@/lib/utils"

interface PurchaseCardMobileProps {
  onPurchase: (planId: string) => void;
  onScrollToPlans: () => void;
  isFloatingCountdownVisible?: boolean;
  onOpenPromo?: () => void;
}

const PurchaseCardMobile = ({ onPurchase, onScrollToPlans, isFloatingCountdownVisible, onOpenPromo }: PurchaseCardMobileProps) => {
  const { t, i18n } = useTranslation()
  const { plans, isLoading } = usePlans();
  const { referralCode } = usePromoCode();
  const [isVisible, setIsVisible] = useState(false);

  const annualPlan = plans.find(plan => plan.id === 'annual');

  const trialPeriodDays = annualPlan?.pricing.trialPeriodDays;

  useEffect(() => {
    if (!isLoading && annualPlan) {
      setIsVisible(true);
    }
  }, [isLoading, annualPlan]);

  return (
    <>
      {/* Mobile Purchase Card - Fixed Bottom */}
      <div
        className={`
          lg:hidden w-screen fixed bottom-0 left-0 bg-white border-t shadow-2xl z-50 border-slate-200
          transition-all duration-300 ease-out transform
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
        `}
      >
        <PromoBanner className="block lg:hidden -top-10" isVisible={!isFloatingCountdownVisible} onOpen={() => onOpenPromo?.()} />
        <FloatingCountdown className="block lg:hidden -top-9" />
        <div className="container mx-auto px-4 pt-1.5 pb-2 max-w-lg">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col items-start gap-0.5">

              <p className="text-xs text-red-500">
                {t(annualPlan?.pricing.discount?.label || '') + ' ' + formatDiscount(annualPlan?.pricing.discount?.percentage || 0, i18n.language)}
              </p>


              {annualPlan?.pricing.current && !isLoading && (
                <div className="flex flex-row items-baseline justify-center gap-2">
                  <span className="text-xl font-semibold text-red-500">
                    {annualPlan?.pricing.currency} {Number(annualPlan?.pricing.current).toLocaleString()}
                  </span>
                  {/* <span className="text-xs text-red-500">
                    / {t('plans.annual.unit')}
                  </span> */}
                </div>
              )}

              {annualPlan?.pricing.original && !isLoading && (
                <div className="flex flex-row items-baseline justify-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-400">
                    {t('plans.annual.planType')}
                  </span>
                  <span className="text-xs text-slate-400 line-through">
                    {annualPlan?.pricing.currency} {Number(annualPlan?.pricing.original).toLocaleString()}
                  </span>
                  {/* <Badge className="mt-1 text-xs bg-red-100 text-red-700 hover:bg-red-100">
                    {t(annualPlan?.pricing.discount?.label || '') + ' ' + formatDiscount(annualPlan?.pricing.discount?.percentage || 0, i18n.language)}
                  </Badge> */}
                </div>
              )}

              {/** Promo code savings hint */}
              {/* {(annualPlan as any)?.pricing?.promoSavings && !isLoading && (
                <span className="text-xs text-slate-400 mt-0.5">
                  {t('common.referralCodeApplied', {
                    code: (annualPlan as any).pricing.promoCode,
                    currency: annualPlan?.pricing.currency,
                    amount: Number((annualPlan as any).pricing.promoSavings).toLocaleString()
                  })}
                </span>
              )} */}
            </div>

            <div className="flex flex-col gap-0 flex-grow mt-1">
              <Button
                className="h-full bg-yellow-300 hover:bg-yellow-400 px-2 text-base text-slate-800 border-b-4 border-yellow-400"
                onClick={() => {
                  track(EVENTS.INITIATE_PURCHASE, {
                    planId: 'annual',
                    planPrice: annualPlan?.pricing.current,
                    currency: annualPlan?.pricing.currency,
                    promoCode: referralCode ?? undefined,
                    stripeLookupKey: getStripeLookupKey('annual')
                  });
                  onPurchase('annual');
                }}
              >
                {trialPeriodDays
                  ? t('purchase.cta.startTrial', { days: trialPeriodDays })
                  : t('purchase.cta.subscribe')}
              </Button>

              <Button
                variant="link"
                className="text-xs text-slate-400 py-1 h-8 underline w-full"
                onClick={() => {
                  onScrollToPlans();
                }}
              >
                {t('purchase.cta.viewAllPlans')}
              </Button>

              {/* <Button
                className="h-full bg-yellow-300 hover:bg-yellow-400 px-2 text-base text-slate-800 border-b-4 border-yellow-400"
                onClick={() => {
                  onScrollToPlans();
                }}
              >
                {t('purchase.cta.viewAllPlans')}
              </Button> */}

            </div>
          </div>
        </div>
      </div>

      {/* Add bottom padding for mobile to account for fixed purchase card */}
      <div className={`
        lg:hidden h-20
        transition-opacity duration-500
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `}></div>
    </>
  )
}

export default PurchaseCardMobile